/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type PropsWithChildren } from 'react';

import { css, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { TOOLTIP_USERS_LIMIT } from '../shared/constants';
import { messages } from '../shared/i18n';
import { type ReactionSummary } from '../types';
import { type OpenReactionsDialogOptions } from './Reactions';

export const verticalMargin = 5;
const tooltipStyle = css({
	maxWidth: '150px',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	marginBottom: verticalMargin,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	ul: {
		listStyle: 'none',
		margin: 0,
		padding: 0,
		textAlign: 'left',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	li: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		marginTop: verticalMargin,
	},
});

const wideTooltipStyle = css({
	maxWidth: '320px',
});

const emojiNameStyle = css({
	textTransform: 'capitalize',
	color: token('color.text.inverse'),
	fontWeight: token('font.weight.semibold'),
});

const footerStyle = css({
	color: token('color.text.inverse'),
});

const underlineStyle = css({
	cursor: 'pointer',
	textDecoration: 'underline',
	'&:hover': {
		backgroundColor: token('color.background.neutral.bold'),
		color: token('color.text.inverse'),
	},
	transition: token('motion.button.hovered'),
});

// Resets native <button> chrome so it renders as inline text within the tooltip list.
const footerButtonStyle = css({
	backgroundColor: 'transparent',
	border: 'none',
	font: token('font.body.small'),
	display: 'block',
	paddingTop: token('space.0'),
	paddingRight: token('space.0'),
	paddingBottom: token('space.0'),
	paddingLeft: token('space.0'),
	width: '100%',
	textAlign: 'left',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const footerButtonColorStyle = css({
	color: token('color.text.inverse'),
});

/**
 * Test id for wrapper ReactionTooltip div
 */
export const RENDER_REACTIONTOOLTIP_TESTID = 'render-reactionTooltip';

export type ReactionTooltipProps = PropsWithChildren<{
	/**
	 * Optional prop for enabling the Reactions Dialog
	 */
	allowUserDialog?: boolean;
	/**
	 * Function that hides the tooltip
	 */
	dismissTooltip: () => void;
	/**
	 * Optional name for the reaction emoji
	 */
	emojiName?: string;
	/**
	 * Optional function when the user wants to open the Reactions Dialog
	 */
	handleOpenReactionsDialog?: (options?: OpenReactionsDialogOptions) => void;
	/**
	 * Optional flag for enabling tooltip (defaults to true)
	 */
	isEnabled?: boolean;
	/**
	 * Optional Max users to show in the displayed tooltip (defaults to 5)
	 */
	maxReactions?: number;
	/**
	 * Info on the emoji reaction to render
	 */
	reaction: ReactionSummary;
	wideTooltip?: boolean;
}>;

export const ReactionTooltip = ({
	children,
	emojiName,
	reaction: { users = [] },
	maxReactions = TOOLTIP_USERS_LIMIT,
	isEnabled = true,
	allowUserDialog,
	handleOpenReactionsDialog,
	dismissTooltip,
	wideTooltip = false,
}: ReactionTooltipProps): JSX.Element => {
	const handleClick = () => {
		if (allowUserDialog && handleOpenReactionsDialog) {
			dismissTooltip();
			handleOpenReactionsDialog();
		}
	};

	const hasOverflow = users.length > maxReactions;
	// Only render the footer <li> when there is overflow text to show; an empty <li> adds
	// unwanted vertical space at the bottom of the tooltip.
	const shouldRenderFooter = hasOverflow || !fg('platform_reactions_tooltip_hide_empty_footer');

	const content =
		!users || users.length === 0 || !isEnabled ? null : (
			// eslint-disable-next-line @atlassian/a11y/no-noninteractive-tabindex
			<div
				css={[tooltipStyle, wideTooltip && wideTooltipStyle]}
				tabIndex={fg('platform_suppression_removal_fix_reactions') ? undefined : 0}
			>
				<ul>
					{emojiName ? <li css={emojiNameStyle}>{emojiName}</li> : null}
					{users.slice(0, maxReactions).map((user) => (
						<li key={user.id}>
							{user.agent ? (
								<FormattedMessage
									{...messages.agentWithPerson}
									values={{ agentName: user.agent.name, personName: user.displayName }}
								/>
							) : (
								user.displayName
							)}
						</li>
					))}
					{/* If count of reactions higher than given threshold then render custom message */}
					{shouldRenderFooter ? (
						<li css={footerStyle}>
							{hasOverflow &&
								(allowUserDialog && handleOpenReactionsDialog ? (
									<button
										type="button"
										css={[footerButtonStyle, footerButtonColorStyle, underlineStyle]}
										onClick={handleClick}
										onMouseDown={(e) => e.preventDefault()}
									>
										<FormattedMessage
											{...messages.otherUsers}
											values={{
												count: users.length - maxReactions,
											}}
										/>
									</button>
								) : (
									<FormattedMessage
										{...messages.otherUsers}
										values={{
											count: users.length - maxReactions,
										}}
									/>
								))}
						</li>
					) : null}
				</ul>
			</div>
		);

	return (
		<Tooltip content={content} position="bottom" testId={RENDER_REACTIONTOOLTIP_TESTID}>
			{React.Children.only(children)}
		</Tooltip>
	);
};
